/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  Alert,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  List,
  ListItem
} from '@mui/material';
import PhoneInput from '../shared/PhoneInput';

const money = (v) => Number(v || 0).toFixed(2);

// Mail servers commonly reject anything past 25MB, so flag well before that.
const ATTACHMENT_WARNING_BYTES = 15 * 1024 * 1024;

const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '');

/**
 * Finalize & Send review.
 *
 * Finalizing sends the invoice to the project team and starts the signing
 * process. It is also where the admin verifies their half of the business-office
 * billing request, step by step: who it is opened on behalf of, what is being
 * charged, and the MOU / purchase order / additional details. The customer half
 * (payers, accounts, billing contacts) is filled in once everyone has signed.
 */
const FinalizeInvoiceDialog = ({ open, invoice, project, invoiceNumber, onClose, onFinalized }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [details, setDetails] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  const projectRef = project?.project_id || project?._id;

  const loadDetails = useCallback(async () => {
    if (!open || !invoice || !projectRef) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `/api/invoices/${projectRef}/${invoice.invoice_id}/billing-request`
      );
      setDetails(res.data);
      setActiveStep(0);
    } catch (error) {
      console.error('Error loading billing request details:', error);
      toast.error(error.response?.data?.message || 'Failed to load billing request details');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [open, invoice, projectRef, onClose]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const update = (field, value) => setDetails(prev => ({ ...prev, [field]: value }));

  const requesterComplete =
    !!details?.requester_name?.trim() && !!details?.requester_email?.trim();

  const serviceItems = details?.service_items || [];
  const serviceItemsTotal = serviceItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const itemsReconcile = Math.abs(serviceItemsTotal - Number(details?.total_amount || 0)) < 0.01;

  const involvesMou = details?.involves_mou === 'Yes';
  const availableDocuments = details?.available_documents || [];
  const maxDocuments = details?.max_mou_documents || 3;
  const selectedDocumentIds = details?.mou_document_ids || [];
  const selectedDocuments = availableDocuments.filter(
    doc => selectedDocumentIds.includes(String(doc._id))
  );
  const attachmentBytes = selectedDocuments.reduce((sum, doc) => sum + Number(doc.file_size || 0), 0);

  const toggleDocument = (docId) => {
    const id = String(docId);
    const next = selectedDocumentIds.includes(id)
      ? selectedDocumentIds.filter(existing => existing !== id)
      : [...selectedDocumentIds, id];
    update('mou_document_ids', next);
  };

  const handleConfirm = async () => {
    if (!requesterComplete) {
      toast.error('A requester name and email address are required.');
      setActiveStep(0);
      return;
    }
    setSubmitting(true);
    try {
      await axios.patch(`/api/invoices/${projectRef}/${invoice.invoice_id}/finalize`, {
        billing_request: {
          requester_name: details.requester_name,
          requester_ouc: details.requester_ouc,
          requester_phone: details.requester_phone,
          requester_email: details.requester_email,
          involves_mou: details.involves_mou,
          purchase_order_number: details.purchase_order_number,
          mou_document_ids: involvesMou ? selectedDocumentIds : [],
          additional_details: details.additional_details
        }
      });
      toast.success('Invoice finalized and sent to users');
      onFinalized();
    } catch (error) {
      console.error('Error finalizing invoice:', error);
      toast.error(error.response?.data?.message || 'Failed to finalize invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const stepActions = (nextLabel, nextDisabled) => (
    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
      <Button
        variant="contained"
        size="small"
        onClick={() => setActiveStep(s => s + 1)}
        disabled={nextDisabled}
      >
        {nextLabel}
      </Button>
      {activeStep > 0 && (
        <Button size="small" onClick={() => setActiveStep(s => s - 1)}>
          Back
        </Button>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Finalize &amp; Send Invoice #{invoiceNumber}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {project?.project_title || project?.project_id}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading || !details ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2.5 }}>
              Verify the details below before sending. Once confirmed the project team can see this
              invoice, the PI splits the amount, each payer selects their accounts and signs, and a
              billing request is then emailed to the business office for each payer. You can still
              edit the invoice while it is Billed.
            </Alert>

            <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
              {/* ── Step 1: who the request is opened on behalf of ── */}
              <Step completed={requesterComplete}>
                <StepLabel
                  onClick={() => setActiveStep(0)}
                  sx={{ cursor: 'pointer' }}
                  optional={
                    activeStep !== 0 && requesterComplete ? (
                      <Typography variant="caption" color="text.secondary">
                        {details.requester_name} · OUC {details.requester_ouc || '—'}
                      </Typography>
                    ) : null
                  }
                >
                  <Typography sx={{ fontWeight: 600 }}>Billed by</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Prefilled from the admin who generated this invoice. Sent to the business office
                    as &ldquo;Open on behalf of this user&rdquo; so they know who raised the request
                    and how to reach them.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="User Name"
                        value={details.requester_name || ''}
                        onChange={(e) => update('requester_name', e.target.value)}
                        error={!details.requester_name?.trim()}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="OUC"
                        value={details.requester_ouc || ''}
                        onChange={(e) => update('requester_ouc', e.target.value)}
                        helperText="From the requester's department"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel shrink>User's Phone Number</InputLabel>
                      <PhoneInput
                        value={details.requester_phone || ''}
                        onChange={(v) => update('requester_phone', v)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="User's Email Address"
                        value={details.requester_email || ''}
                        onChange={(e) => update('requester_email', e.target.value)}
                        error={!details.requester_email?.trim()}
                      />
                    </Grid>
                  </Grid>
                  {stepActions('Looks Correct', !requesterComplete)}
                </StepContent>
              </Step>

              {/* ── Step 2: what is being charged and where the revenue lands ── */}
              <Step completed={activeStep > 1}>
                <StepLabel
                  onClick={() => requesterComplete && setActiveStep(1)}
                  sx={{ cursor: requesterComplete ? 'pointer' : 'default' }}
                  optional={
                    activeStep !== 1 ? (
                      <Typography variant="caption" color="text.secondary">
                        {details.revenue_project_phase || 'No revenue account'} · $
                        {money(details.total_amount)}
                      </Typography>
                    ) : null
                  }
                >
                  <Typography sx={{ fontWeight: 600 }}>Revenue &amp; charges</Typography>
                </StepLabel>
                <StepContent>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Project-Phase to receive the revenue
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {details.revenue_project_phase || 'Not set'}
                    </Typography>
                    {details.revenue_facility && (
                      <Chip size="small" label={details.revenue_facility} sx={{ mt: 0.5 }} />
                    )}
                  </Paper>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 60 }}>Item</TableCell>
                          <TableCell>Description of Service/Item</TableCell>
                          <TableCell align="right">Dollar Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {serviceItems.map((item, i) => (
                          <TableRow key={i}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell align="right">{money(item.amount)}</TableCell>
                          </TableRow>
                        ))}
                        {serviceItems.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="body2" color="text.secondary">
                                No billable service items found.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                            TOTAL DOLLAR AMOUNT
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            $ {money(details.total_amount)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {!itemsReconcile && serviceItems.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      The service items add up to ${money(serviceItemsTotal)} but the invoice total is
                      ${money(details.total_amount)}. Check the invoice before sending it.
                    </Alert>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    The customer, their department, their billing person and the accounts to be
                    charged are filled in automatically from each payer once they sign. Each payer
                    receives their own billing request, prorated to their share.
                  </Typography>
                  {stepActions('Looks Correct', false)}
                </StepContent>
              </Step>

              {/* ── Step 3: the trailing questions on the billing request form ── */}
              <Step completed={activeStep > 2}>
                <StepLabel
                  onClick={() => requesterComplete && setActiveStep(2)}
                  sx={{ cursor: requesterComplete ? 'pointer' : 'default' }}
                  optional={
                    activeStep !== 2 ? (
                      <Typography variant="caption" color="text.secondary">
                        MOU / TSA / Contract: {details.involves_mou || 'No'}
                        {involvesMou && selectedDocuments.length > 0 &&
                          ` · ${selectedDocuments.length} document${selectedDocuments.length > 1 ? 's' : ''} attached`}
                      </Typography>
                    ) : null
                  }
                >
                  <Typography sx={{ fontWeight: 600 }}>Additional billing request details</Typography>
                </StepLabel>
                <StepContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>MOU / TSA / Contract</InputLabel>
                        <Select
                          value={details.involves_mou || 'No'}
                          onChange={(e) => update('involves_mou', e.target.value)}
                          label="MOU / TSA / Contract"
                        >
                          <MenuItem value="No">No</MenuItem>
                          <MenuItem value="Yes">Yes</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Purchase Order Number"
                        value={details.purchase_order_number || ''}
                        onChange={(e) => update('purchase_order_number', e.target.value)}
                        placeholder="If applicable"
                      />
                    </Grid>
                    {involvesMou && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Attach the paperwork
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Pick up to {maxDocuments} files from this project&apos;s Documents
                            section. They go to the business office with every payer&apos;s billing
                            request, alongside the invoice PDF.
                          </Typography>

                          {availableDocuments.length === 0 ? (
                            <Alert severity="info">
                              This project has no uploaded documents yet. Add the MOU, TSA or
                              contract in the project&apos;s Documents section, then reopen this
                              dialog.
                            </Alert>
                          ) : (
                            <>
                              <List dense disablePadding>
                                {availableDocuments.map(doc => {
                                  const id = String(doc._id);
                                  const checked = selectedDocumentIds.includes(id);
                                  const atLimit =
                                    !checked && selectedDocumentIds.length >= maxDocuments;
                                  return (
                                    <ListItem key={id} disableGutters sx={{ py: 0.25 }}>
                                      <FormControlLabel
                                        sx={{ alignItems: 'flex-start', m: 0 }}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={checked}
                                            disabled={atLimit}
                                            onChange={() => toggleDocument(id)}
                                            sx={{ pt: 0.25 }}
                                          />
                                        }
                                        label={
                                          <Box>
                                            <Typography variant="body2">
                                              {doc.original_filename}
                                              {doc.label && (
                                                <Chip
                                                  size="small"
                                                  label={doc.label}
                                                  sx={{ ml: 1, height: 18 }}
                                                />
                                              )}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {formatFileSize(doc.file_size)} &middot; uploaded{' '}
                                              {formatDate(doc.createdAt)}
                                            </Typography>
                                          </Box>
                                        }
                                      />
                                    </ListItem>
                                  );
                                })}
                              </List>

                              <Typography variant="caption" color="text.secondary">
                                {selectedDocumentIds.length} of {maxDocuments} selected
                                {attachmentBytes > 0 && ` · ${formatFileSize(attachmentBytes)}`}
                              </Typography>

                              {attachmentBytes > ATTACHMENT_WARNING_BYTES && (
                                <Alert severity="warning" sx={{ mt: 1.5 }}>
                                  These attachments total {formatFileSize(attachmentBytes)} before
                                  the invoice PDF is added. Large emails are often rejected &mdash;
                                  consider attaching fewer files.
                                </Alert>
                              )}
                            </>
                          )}
                        </Paper>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        size="small"
                        label="Additional Descriptions / Details and/or Contacts"
                        value={details.additional_details || ''}
                        onChange={(e) => update('additional_details', e.target.value)}
                        helperText="The account split for each payer is appended here automatically."
                      />
                    </Grid>
                  </Grid>
                  {stepActions('Review & Send', false)}
                </StepContent>
              </Step>

              {/* ── Step 4: confirm ── */}
              <Step>
                <StepLabel>
                  <Typography sx={{ fontWeight: 600 }}>Send to the project team</Typography>
                </StepLabel>
                <StepContent>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Invoice #{invoiceNumber} for ${money(details.total_amount)} will become visible to
                    the project team, and the PI will be able to set up the payment split.
                  </Alert>
                  {involvesMou && selectedDocuments.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Sent to the business office with each billing request, once everyone signs
                      </Typography>
                      {selectedDocuments.map(doc => (
                        <Chip
                          key={String(doc._id)}
                          size="small"
                          label={`${doc.original_filename} (${formatFileSize(doc.file_size)})`}
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Paper>
                  )}
                  <Button size="small" onClick={() => setActiveStep(2)}>
                    Back
                  </Button>
                </StepContent>
              </Step>
            </Stepper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={loading || submitting || !details || !requesterComplete || activeStep < 3}
        >
          {submitting ? 'Finalizing...' : 'Finalize & Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalizeInvoiceDialog;
